/**
 * Stage 5: Review Your Trip
 *
 * Summary page showing all booking details for review before proceeding to terms/payment.
 * Allows editing previous stages via edit buttons.
 *
 * Location: src/components/booking/flow/stages/Stage5Requirements.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * Features:
 * - Trip details card (guide, service, date, guests, duration)
 * - Contact information summary
 * - Edit buttons to navigate back to previous stages
 * - Age and dietary requirements summary
 * - Navigation buttons (Back/Continue)
 */

import React from 'react';

/**
 * Stage5Requirements Component (renamed to Stage5ReviewTrip in practice)
 *
 * @param {Object} props
 * @param {Object} props.bookingData - Current booking form data
 * @param {Function} props.onNext - Handler for proceeding to next stage
 * @param {Function} props.onBack - Handler for going back
 * @param {Function} props.trackProgressAction - Handler for tracking progress
 * @param {Function} props.onEditStage - Handler for navigating to specific stage (for edit buttons)
 * @returns {JSX.Element}
 */
export default function Stage5Requirements({ bookingData, onNext, onBack, trackProgressAction, onEditStage }) {
  const handleSubmit = (  _e) => {
    _e.preventDefault();

    // Track progress action
    if (trackProgressAction) {
      trackProgressAction(5, 'step_advanced');
    }

    // No validation needed - this is just a review page
    onNext({});
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) {return '-';}
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Map dietary restriction codes to readable text
  const dietaryRestrictionText = {
    'vegetarian': 'Vegetarian',
    'vegan': 'Vegan',
    'gluten-free': 'Gluten-Free',
    'other': 'Other dietary needs',
    '': 'None'
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Review Your Trip</h2>
          <p className="text-gray-600 mt-1">Make sure everything looks correct</p>
        </div>

        {/* Trip Details Card */}
        <div className="border rounded-xl overflow-hidden">
          <div className="p-4 bg-gray-50">
            <p className="font-semibold text-gray-900">Trip Details</p>
          </div>
          <div className="p-4 space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Date</span>
              <span className="font-medium">{formatDate(bookingData.startDate || bookingData.date)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Guests</span>
              <span className="font-medium">{bookingData.partySize || bookingData.guests || 1} adults</span>
            </div>
            <button
              type="button"
              onClick={() => onEditStage && onEditStage(1)}
              className="text-emerald-600 text-sm font-medium mt-2 hover:text-emerald-700"
            >
              Edit →
            </button>
          </div>
        </div>

        {/* Contact Information */}
        <div className="border rounded-xl p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-gray-900">Contact Information</p>
            <button
              type="button"
              onClick={() => onEditStage && onEditStage(2)}
              className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
            >
              Edit →
            </button>
          </div>
          <p>
            <span className="text-gray-500">Name:</span>{' '}
            <span className="font-medium">
              {bookingData.customerFirstName || bookingData.firstName || '-'} {bookingData.customerLastName || bookingData.lastName || '-'}
            </span>
          </p>
          <p>
            <span className="text-gray-500">Email:</span>{' '}
            <span className="font-medium">{bookingData.customerEmail || bookingData.email || '-'}</span>
          </p>
          {bookingData.smsConsent && bookingData.phoneNumber && (
            <p>
              <span className="text-gray-500">Phone:</span>{' '}
              <span className="font-medium">{bookingData.phoneNumber}</span>
            </p>
          )}
          {bookingData.smsConsent && (
            <p className="text-xs text-gray-500 mt-2">
              ✓ SMS notifications enabled
            </p>
          )}
        </div>

        {/* Trip Requirements */}
        <div className="border rounded-xl p-4 space-y-2 text-sm">
          <div className="flex items-center justify-between mb-2">
            <p className="font-medium text-gray-900">Trip Requirements</p>
            <button
              type="button"
              onClick={() => onEditStage && onEditStage(4)}
              className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
            >
              Edit →
            </button>
          </div>

          {/* Age Verification */}
          {bookingData.ageVerified && (
            <p className="text-xs text-gray-500">
              ✓ All participants meet age requirements
            </p>
          )}

          {/* Dietary Restrictions */}
          {bookingData.dietaryRestrictions && (
            <p>
              <span className="text-gray-500">Dietary:</span>{' '}
              <span className="font-medium">
                {dietaryRestrictionText[bookingData.dietaryRestrictions] || bookingData.dietaryRestrictions}
              </span>
            </p>
          )}

          {/* Questions for Guide */}
          {bookingData.questionsForGuide && (
            <div className="mt-2 pt-2 border-t">
              <p className="text-gray-500 mb-1">Questions for Guide:</p>
              <p className="text-gray-700 italic text-xs bg-gray-50 p-2 rounded">
                &quot;{bookingData.questionsForGuide}&quot;
              </p>
            </div>
          )}

          {!bookingData.dietaryRestrictions && !bookingData.questionsForGuide && !bookingData.ageVerified && (
            <p className="text-xs text-gray-500">No special requirements</p>
          )}
        </div>

        {/* Info Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            After you continue, you&apos;ll review the cancellation policy and complete payment.
          </p>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Continue to Terms
          </button>
        </div>
      </form>
    </div>
  );
}
